// Modal de criação rápida de contato. Form validado com zod + react-hook-form.
import { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { TagInput } from '@/components/ui/TagInput';
import { useToast } from '@/components/ui/Toast';
import { maskPhoneInputBR } from '@/lib/utils/formatters';
import { useListStoresQuery } from '@/features/stores';
import {
  useCreateContactMutation,
  useGetContactTagsQuery,
  type ContactPhoneExistsError,
} from '../api';
import styles from './ContactCreateModal.module.css';

const schema = z.object({
  name: z.string().min(2, 'Nome muito curto').max(120),
  phone: z
    .string()
    .min(8, 'Telefone inválido')
    .regex(/^[+\d\s()-]+$/, 'Use apenas dígitos e separadores comuns'),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  storeId: z.string().optional(),
  tags: z.array(z.string()),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (id: string) => void;
  /**
   * Pré-preenchimento ao abrir (ex.: 'Iniciar conversa' num número que o cliente mandou
   * e ainda não está no CRM). Telefone sem o 55, só dígitos.
   */
  initialValues?: { name?: string; phone?: string };
}

/** Type guard pro corpo do 409 (telefone duplicado). */
function isPhoneExistsError(err: unknown): err is { data: ContactPhoneExistsError } {
  return (
    typeof err === 'object' &&
    err !== null &&
    'data' in err &&
    typeof (err as { data?: unknown }).data === 'object' &&
    (err as { data?: { code?: unknown } }).data?.code === 'CONTACT_PHONE_EXISTS'
  );
}

export function ContactCreateModal({ open, onClose, onCreated, initialValues }: Props) {
  const [createContact, { isLoading }] = useCreateContactMutation();
  // Stores/tags só interessam com o modal aberto — evita fetch desnecessário.
  const { data: stores } = useListStoresQuery(undefined, { skip: !open });
  const { data: tagSuggestions = [] } = useGetContactTagsQuery(undefined, { skip: !open });
  const toast = useToast();

  const storeItems = useMemo(() => stores?.items ?? [], [stores]);
  const singleStoreId = storeItems.length === 1 ? storeItems[0].id : undefined;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', phone: '', email: '', storeId: '', tags: [] },
  });

  const initialName = initialValues?.name ?? '';
  const initialPhone = initialValues?.phone ?? '';
  useEffect(() => {
    if (!open || (!initialName && !initialPhone)) return;
    reset({
      name: initialName,
      phone: maskPhoneInputBR(initialPhone),
      email: '',
      storeId: '',
      tags: [],
    });
  }, [open, initialName, initialPhone, reset]);

  const close = () => {
    reset();
    onClose();
  };

  const submit = handleSubmit(async (values) => {
    const storeId = singleStoreId ?? values.storeId ?? undefined;
    try {
      const created = await createContact({
        name: values.name,
        phone: values.phone.replace(/\D/g, ''),
        email: values.email || undefined,
        storeId: storeId || undefined,
        tags: values.tags.length > 0 ? values.tags : undefined,
      }).unwrap();
      reset();
      toast.success({ title: 'Contato criado', description: values.name });
      onCreated?.(created.id);
      onClose();
    } catch (err) {
      if (isPhoneExistsError(err)) {
        toast.warning({ title: 'Esse telefone já tem contato' });
        const existingId = err.data.contactId;
        if (existingId) {
          onCreated?.(existingId);
          close();
        }
        return;
      }
      toast.danger({ title: 'Não conseguimos salvar', description: 'Tente novamente.' });
    }
  });

  return (
    <Modal open={open} onClose={close} title="Novo contato">
      <form className={styles.form} onSubmit={submit} noValidate>
        <Input
          label="Nome *"
          placeholder="Ex.: Ana Beatriz"
          error={errors.name?.message}
          {...register('name')}
        />
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <Input
              label="Telefone *"
              placeholder="(11) 9 9999-0000"
              inputMode="tel"
              error={errors.phone?.message}
              name={field.name}
              ref={field.ref}
              value={field.value ?? ''}
              onBlur={field.onBlur}
              onChange={(e) => field.onChange(maskPhoneInputBR(e.currentTarget.value))}
            />
          )}
        />
        <Input
          label="E-mail"
          type="email"
          placeholder="opcional"
          error={errors.email?.message}
          {...register('email')}
        />
        {singleStoreId ? (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Empresa</span>
            <p className={styles.fieldValue}>{storeItems[0].name}</p>
          </div>
        ) : (
          storeItems.length > 1 && (
            <Controller
              control={control}
              name="storeId"
              render={({ field }) => (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Empresa</span>
                  <Select
                    aria-label="Empresa"
                    options={[
                      { value: '', label: 'Selecione a empresa' },
                      ...storeItems.map((s) => ({ value: s.id, label: s.name })),
                    ]}
                    name={field.name}
                    ref={field.ref}
                    value={field.value ?? ''}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                  />
                </div>
              )}
            />
          )
        )}
        <Controller
          control={control}
          name="tags"
          render={({ field }) => (
            <TagInput
              label="Tags"
              placeholder="Adicionar tag…"
              value={field.value}
              onChange={field.onChange}
              suggestions={tagSuggestions}
            />
          )}
        />
        <div className={styles.actions}>
          <Button variant="ghost" type="button" onClick={close}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" loading={isLoading || isSubmitting}>
            Criar contato
          </Button>
        </div>
      </form>
    </Modal>
  );
}
